/**
 * 电商套图 · 配置阶段主区素材面板
 *
 * 商品真值图（首张为主图，像素保护依据）/ 风格参考图 双 tab。
 * 上传与删除即时自动保存（逐张落盘为 canonical key），无需手动点保存；
 * 保存失败自动回滚到最近一次服务端快照并提示。
 */
import { useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import toast from '../../../components/Toast';
import { suiteTheme } from '../theme';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import type { SuiteProjectDetail } from '../api';

type AssetTab = 'product' | 'reference';

interface AssetLists {
  product: string[];
  reference: string[];
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target?.result as string);
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.readAsDataURL(file);
  });
}

function appendTo(lists: AssetLists, tab: AssetTab, item: string): AssetLists {
  return tab === 'product'
    ? { ...lists, product: [...lists.product, item] }
    : { ...lists, reference: [...lists.reference, item] };
}

function removeFrom(lists: AssetLists, tab: AssetTab, index: number): AssetLists {
  return tab === 'product'
    ? { ...lists, product: lists.product.filter((_, i) => i !== index) }
    : { ...lists, reference: lists.reference.filter((_, i) => i !== index) };
}

export function AssetsPanel({
  project,
  onSave,
  onPreview,
}: {
  project: SuiteProjectDetail;
  onSave: (payload: { productImages: string[]; referenceImages: string[] }) => Promise<SuiteProjectDetail | null>;
  onPreview: (image: string) => void;
}) {
  const [tab, setTab] = useState<AssetTab>('product');
  const [lists, setLists] = useState<AssetLists>({
    product: project.productImageList,
    reference: project.referenceImageList,
  });
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const listsRef = useRef<AssetLists>(lists);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxCount = 8;

  const applyLists = (next: AssetLists) => {
    listsRef.current = next;
    setLists(next);
  };

  const persist = async (next: AssetLists, fallback: AssetLists) => {
    applyLists(next);
    setSaving(true);
    try {
      const detail = await onSave({ productImages: next.product, referenceImages: next.reference });
      applyLists(detail ? { product: detail.productImageList, reference: detail.referenceImageList } : fallback);
    } catch {
      applyLists(fallback);
    } finally {
      setSaving(false);
    }
  };

  const handleFiles = async (files: FileList | File[]) => {
    if (saving) return;
    const picked = Array.from(files)
      .filter((file) => file.type.startsWith('image/'))
      .filter((file) => file.size <= 10 * 1024 * 1024);
    if (picked.length === 0) return;

    // 逐张顺序提交：每请求只带 1 张新图，成功后回写 canonical 列表再传下一张
    for (const file of picked) {
      const current = listsRef.current;
      if (current[tab].length >= maxCount) {
        toast.error(tab === 'product' ? `产品图最多 ${maxCount} 张` : `参考图最多 ${maxCount} 张`);
        break;
      }
      let dataUrl: string;
      try {
        dataUrl = await readFileAsDataUrl(file);
      } catch {
        toast.error('读取文件失败');
        continue;
      }
      await persist(appendTo(current, tab, dataUrl), current);
    }
  };

  const removeAt = (index: number) => {
    if (saving) return;
    const current = listsRef.current;
    void persist(removeFrom(current, tab, index), current);
  };

  const current = tab === 'product' ? lists.product : lists.reference;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex overflow-hidden rounded-xl border border-slate-200 bg-white">
          {(
            [
              { key: 'product', label: '产品图' },
              { key: 'reference', label: '参考图' },
            ] as const
          ).map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`px-8 py-2.5 text-sm font-medium transition ${
                tab === item.key
                  ? 'bg-blue-100 text-blue-600'
                  : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        {saving ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-4 py-2 text-sm font-medium text-blue-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            保存中
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-4 py-2 text-sm text-zinc-500">
            {current.length}/{maxCount}
          </span>
        )}
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void handleFiles(e.dataTransfer.files);
        }}
        onClick={() => {
          if (!saving) fileInputRef.current?.click();
        }}
        className={`flex min-h-[180px] cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed px-6 py-10 text-center transition ${
          dragging ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:border-blue-300/60'
        } ${saving ? 'pointer-events-none opacity-60' : ''}`}
      >
        <ImagePlus className="h-8 w-8 text-zinc-500" />
        <p className="text-sm font-medium text-zinc-600">
          {tab === 'product' ? '拖入产品图' : '拖入风格参考图'}
        </p>
        <p className="max-w-md text-xs leading-relaxed text-zinc-500">
          {tab === 'product'
            ? '商品外观与可见细节，像素保护必须使用这些图。首张为主图，上传即自动保存。'
            : '参考图仅用于画面风格参考，不会替代商品本体，上传即自动保存。'}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {current.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {current.map((image, index) => (
            <div key={`${image.slice(0, 32)}-${index}`} className={`group relative overflow-hidden rounded-xl border border-slate-200 bg-slate-100 ${suiteTheme.cardHover}`}>
              <button type="button" onClick={() => onPreview(image)} className="block aspect-square w-full">
                <img src={resolveBrowserMediaUrl(image)} alt={tab === 'product' ? `产品图 ${index + 1}` : `参考图 ${index + 1}`} className="h-full w-full object-cover" />
              </button>
              {tab === 'product' && index === 0 && (
                <span className="absolute left-2 top-2 rounded-md bg-blue-400 px-1.5 py-0.5 text-xs font-semibold text-zinc-950">主图</span>
              )}
              <button
                type="button"
                onClick={() => removeAt(index)}
                disabled={saving}
                className="absolute right-2 top-2 rounded-lg bg-zinc-950/70 p-1.5 text-zinc-600 opacity-0 transition group-hover:opacity-100 hover:text-rose-600"
                aria-label="删除图片"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
